import React from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Heart, Package, ExternalLink, Mail, HelpCircle, Users, Building, Briefcase, GraduationCap } from 'lucide-react';
export function DonatePage() {
  return <>
      {/* Hero Section */}
      <Section className="py-20" background="offwhite" data-id="element-636">
        <div className="text-center mb-12" data-id="element-637">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-638">
            Support Our Mission
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-639">
            Your generosity helps us deliver critical medical supplies to
            underserved Coptic communities in Egypt.
          </p>
        </div>
      </Section>
      {/* Donation Options */}
      <Section background="white" data-id="element-640">
        <div className="mb-12 text-center" data-id="element-641">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-642">
            Ways to Donate
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-643">
            Choose the donation method that works best for you. Every
            contribution makes a difference.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8" data-id="element-644">
          {/* Financial Donations */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md" data-id="element-645">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-646">
              <Heart size={24} className="text-white" data-id="element-647" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-648">
              Give Financially
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-649">
              Your financial contributions help cover shipping costs, customs
              fees, and operational expenses to ensure medical supplies reach
              those who need them most.
            </p>
            <Button className="w-full" data-id="element-650">Donate Now</Button>
            <p className="text-sm font-inter text-neutral-charcoal mt-4" data-id="element-651">
              All donations are tax-deductible to the extent allowed by law.
            </p>
          </div>
          {/* Amazon Wishlist */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md" data-id="element-652">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-653">
              <ExternalLink size={24} className="text-white" data-id="element-654" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-655">
              Shop Our Amazon Wishlist
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-656">
              Purchase most-needed items that ship directly to us. Please
              include a gift message with your email so we can send an
              acknowledgment.
            </p>
            <Button className="w-full flex items-center justify-center gap-2" onClick={() => window.open('https://www.amazon.com/hz/wishlist/ls/2BKAGMEAOQZHZ?ref_=wl_share', '_blank')} data-id="element-657">
              Open Wish List <ExternalLink size={16} data-id="element-658" />
            </Button>
          </div>
          {/* Donate Medical Supplies */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md" data-id="element-659">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-660">
              <Package size={24} className="text-white" data-id="element-661" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-662">
              Donate Medical Supplies
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-663">
              We accept unopened, unexpired medical supplies. Contact us to
              schedule a pickup or delivery of your donation.
            </p>
            <Button className="w-full flex items-center justify-center gap-2" onClick={() => window.location.href = 'mailto:farashamedicalaid@gmail.com?subject=Medical%20Supply%20Donation%20Inquiry'} data-id="element-664">
              Schedule Pickup/Delivery <Mail size={16} data-id="element-665" />
            </Button>
          </div>
        </div>
      </Section>
      {/* Founder Note */}
      <Section background="sand" data-id="element-666">
        <div className="max-w-4xl mx-auto" data-id="element-667">
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-668">
            <h2 className="text-2xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-669">
              A Note From Our Founder
            </h2>
            <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-670">
              Coming to America has blessed me with incredible opportunities. I
              believe these blessings come with a responsibility to give back
              and help others. Through Farasha Medical Aid, we're channeling
              that spirit of gratitude into tangible support for underserved
              Coptic communities in Egypt.
            </p>
            <p className="text-lg font-inter text-neutral-charcoal" data-id="element-671">
              We're deeply grateful for every donation, volunteer hour, and word
              of encouragement that helps us bridge healthcare gaps and deliver
              hope, one medical supply at a time.
            </p>
          </div>
        </div>
      </Section>
      {/* FAQ for Potential Donors */}
      <Section background="white" data-id="element-672">
        <div className="text-center mb-12" data-id="element-673">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-674">
            Frequently Asked Questions
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-675">
            Answers to common questions about donating to Farasha Medical Aid.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-676">
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-677">
            <div className="flex items-start" data-id="element-678">
              <div className="bg-primary-royal-blue rounded-full p-2 mr-3 mt-1 flex-shrink-0" data-id="element-679">
                <HelpCircle size={18} className="text-white" data-id="element-680" />
              </div>
              <div data-id="element-681">
                <h3 className="font-playfair font-bold text-primary-deep-blue text-xl mb-2" data-id="element-682">
                  Unsure whether to donate?
                </h3>
                <p className="font-inter text-neutral-charcoal" data-id="element-683">
                  Every donation, regardless of size, makes a real difference.
                  Medical supplies that might otherwise go to waste can save
                  lives and improve healthcare for those who need it most. If
                  you're unsure about what to donate, our Amazon Wishlist
                  features our most-needed items, or you can contact us directly
                  for guidance.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-684">
            <div className="flex items-start" data-id="element-685">
              <div className="bg-primary-royal-blue rounded-full p-2 mr-3 mt-1 flex-shrink-0" data-id="element-686">
                <HelpCircle size={18} className="text-white" data-id="element-687" />
              </div>
              <div data-id="element-688">
                <h3 className="font-playfair font-bold text-primary-deep-blue text-xl mb-2" data-id="element-689">
                  Who can donate?
                </h3>
                <p className="font-inter text-neutral-charcoal" data-id="element-690">
                  Anyone can contribute! We welcome donations from individuals,
                  healthcare facilities, manufacturers, corporations, and
                  community organizations. Whether you're a physician with
                  surplus supplies, a clinic updating inventory, a manufacturer
                  with overstock, or simply someone who wants to make a
                  difference, your support is valuable to our mission.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-691">
            <div className="flex items-start" data-id="element-692">
              <div className="bg-primary-royal-blue rounded-full p-2 mr-3 mt-1 flex-shrink-0" data-id="element-693">
                <HelpCircle size={18} className="text-white" data-id="element-694" />
              </div>
              <div data-id="element-695">
                <h3 className="font-playfair font-bold text-primary-deep-blue text-xl mb-2" data-id="element-696">
                  What is the impact?
                </h3>
                <p className="font-inter text-neutral-charcoal" data-id="element-697">
                  Your donations directly support monastery clinics serving
                  underserved Coptic communities in Egypt. These clinics often
                  provide the only accessible healthcare in their regions. By
                  redirecting medical supplies that would otherwise go to waste,
                  you're helping to improve healthcare quality, reduce
                  environmental impact, and most importantly, save lives.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-698">
            <div className="flex items-start" data-id="element-699">
              <div className="bg-primary-royal-blue rounded-full p-2 mr-3 mt-1 flex-shrink-0" data-id="element-700">
                <HelpCircle size={18} className="text-white" data-id="element-701" />
              </div>
              <div data-id="element-702">
                <h3 className="font-playfair font-bold text-primary-deep-blue text-xl mb-2" data-id="element-703">
                  How are donations used?
                </h3>
                <p className="font-inter text-neutral-charcoal" data-id="element-704">
                  Financial donations help cover shipping costs, customs fees,
                  and operational expenses. Physical donations of medical
                  supplies are sorted, verified, packed, and shipped to
                  monastery clinics in Egypt. We maintain rigorous quality
                  standards and ensure all supplies reach their intended
                  destinations where they can be effectively utilized.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>
      {/* Partnership Section */}
      <Section background="sand" data-id="element-705">
        <div className="text-center mb-12" data-id="element-706">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-707">
            Partner With Us
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-708">
            Join our mission by becoming a partner. Together, we can make a
            greater impact.
          </p>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-709">
          <Button variant="secondary" className="flex items-center justify-center gap-2" onClick={() => window.location.href = 'mailto:farashamedicalaid@gmail.com?subject=Partnership%20Inquiry%20-%20Healthcare%20Partner'} data-id="element-710">
            <Users size={16} data-id="element-711" /> Healthcare Partner
          </Button>
          <Button variant="secondary" className="flex items-center justify-center gap-2" onClick={() => window.location.href = 'mailto:farashamedicalaid@gmail.com?subject=Partnership%20Inquiry%20-%20Corporate%20Partner'} data-id="element-712">
            <Building size={16} data-id="element-713" /> Corporate Partner
          </Button>
          <Button variant="secondary" className="flex items-center justify-center gap-2" onClick={() => window.location.href = 'mailto:farashamedicalaid@gmail.com?subject=Partnership%20Inquiry%20-%20Manufacturing%20Partner'} data-id="element-714">
            <Package size={16} data-id="element-715" /> Manufacturing Partner
          </Button>
          <Button variant="secondary" className="flex items-center justify-center gap-2" onClick={() => window.location.href = 'mailto:farashamedicalaid@gmail.com?subject=Partnership%20Inquiry%20-%20Community%20Partner'} data-id="element-716">
            <Users size={16} data-id="element-717" /> Community Partner
          </Button>
          <Button variant="secondary" className="flex items-center justify-center gap-2" onClick={() => window.location.href = 'mailto:farashamedicalaid@gmail.com?subject=Partnership%20Inquiry%20-%20Educational%20Partner'} data-id="element-718">
            <GraduationCap size={16} data-id="element-719" /> Educational Partner
          </Button>
        </div>
      </Section>
      {/* CTA Section */}
      <Section background="deep-blue" data-id="element-720">
        <div className="text-center" data-id="element-721">
          <h2 className="text-3xl font-playfair font-bold mb-4" data-id="element-722">
            Your support changes lives
          </h2>
          <p className="text-xl font-inter mb-8 max-w-3xl mx-auto" data-id="element-723">
            Join us in bridging healthcare gaps and providing essential medical
            supplies to those who need them most.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-724">
            <Button size="lg" data-id="element-725">Donate Now</Button>
            <Button variant="secondary" size="lg" className="flex items-center gap-2" onClick={() => window.open('https://www.amazon.com/hz/wishlist/ls/2BKAGMEAOQZHZ?ref_=wl_share', '_blank')} data-id="element-726">
              Shop Wish List <ExternalLink size={16} data-id="element-727" />
            </Button>
          </div>
          <p className="text-sm mt-6 opacity-80" data-id="element-728">
            Questions about donations? Contact us at{' '}
            <a href="mailto:farashamedicalaid@gmail.com" className="underline hover:text-white" data-id="element-729">
              farashamedicalaid@gmail.com
            </a>
          </p>
        </div>
      </Section>
    </>;
}